import CommonModalBase from '@/components/common/CommonModal'
import { MTFormField, MT_MULTI_VALUE_DELIMITER } from '@/multi-territory/mt-components/mt-shared/MTFormField'
import type { MTMetadataFormField } from '@/multi-territory/mt-types/mt-metadata'
import { buildRegexErrorMessage } from '@/multi-territory/mt-utils/mt-regex-hint'
import { Box, Button, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

const CommonModal = CommonModalBase as any

interface MTServiceKeywordFormProps {
  open: boolean
  onClose: () => void
  title: string
  fields: MTMetadataFormField[]
  onSubmit?: (formData: Record<string, string | number | null>) => Promise<boolean> | void
  initialValues?: Record<string, string> | null
}

export function MTServiceKeywordForm({ open, onClose, title, fields, onSubmit, initialValues }: MTServiceKeywordFormProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      const initial: Record<string, string> = {}
      fields.forEach(f => {
        initial[f.key] = initialValues?.[f.key] ?? ''
      })
      setFormValues(initial)
      setFormErrors({})
    }
  }, [open, fields, initialValues])

  const handleChange = useCallback((key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }))
    setFormErrors(prev => { if (prev[key]) { const next = { ...prev }; delete next[key]; return next } return prev })
  }, [])

  const handleMultiChange = useCallback((key: string, values: string[]) => {
    handleChange(key, values.join(MT_MULTI_VALUE_DELIMITER))
  }, [handleChange])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    fields.forEach(field => {
      const value = (formValues[field.key] || '').trim()

      // Required check
      if (field.required && !value) {
        errors[field.key] = field.validations.required_message || (field.label + ' is required')
        return
      }

      // Skip further validation if empty and not required
      if (!value) return

      // Number-specific validations (min/max)
      if (field.controlType === 'number') {
        const numVal = parseFloat(value)
        if (isNaN(numVal)) {
          errors[field.key] = field.label + ' must be a valid number'
          return
        }
        if (field.validations.min !== undefined && numVal < field.validations.min) {
          errors[field.key] = field.label + ' must be at least ' + field.validations.min
          return
        }
        if (field.validations.max !== undefined && numVal > field.validations.max) {
          errors[field.key] = field.label + ' must not exceed ' + field.validations.max
          return
        }
      }

      // Max length check (for string values)
      if (field.validations.maxLength && value.length > field.validations.maxLength) {
        errors[field.key] = field.label + ' must not exceed ' + field.validations.maxLength + ' characters'
        return
      }

      // Regex validation - skip for dropdown/multiselect/date (they have fixed valid values)
      if (
        field.validations.regex &&
        field.controlType !== 'dropdown' &&
        field.controlType !== 'multiselect' &&
        field.controlType !== 'date'
      ) {
        const pattern = new RegExp(field.validations.regex)
        if (!pattern.test(value)) {
          errors[field.key] = buildRegexErrorMessage(field.label, field.validations.regex, field.validations.regex_message)
          return
        }
      }
    })
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setLoading(true)
    try {
      // Convert form values to proper types based on field type before submitting
      const typedPayload: Record<string, string | number | null> = {}
      fields.forEach(field => {
        const raw = (formValues[field.key] || '').trim()
        if (!raw) {
          typedPayload[field.key] = field.required ? '' : null
          return
        }
        if (field.controlType === 'number') {
          typedPayload[field.key] = parseFloat(raw)
        } else {
          typedPayload[field.key] = raw
        }
      })
      const success = await onSubmit?.(typedPayload)
      if (success !== false) { handleClose() }
    } finally { setLoading(false) }
  }

  const handleClose = () => { setFormValues({}); setFormErrors({}); onClose() }

  // Sort all fields by order for single-column stacked layout
  const sortedFields = [...fields].sort((a, b) => a.order - b.order)


  return (
    <CommonModal open={open} onClose={handleClose} title={title} maxWidth="sm" modalHeight="auto">
      <>
        <Typography variant="body2" sx={{ color: 'var(--forbrand-error, #d12121)', mb: 2, fontSize: '14px' }}>
          (*) Fields are mandatory
        </Typography>
        <Box sx={{ overflowY: 'auto', pr: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', mb: 3 }}>
            {sortedFields.map(field => {
              // For end_date fields, use the start_date value as minDate to disable earlier dates
              const minDate = field.key === 'end_date' ? formValues['start_date'] || undefined : undefined
              return (
                <Box key={field.key}>
                  <MTFormField
                    field={field}
                    value={formValues[field.key] || ''}
                    error={formErrors[field.key]}
                    onChange={handleChange}
                    onMultiChange={handleMultiChange}
                    minDate={minDate}
                  />
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant, #ffaa71)',
              color: 'var(--forbrand-brand-varient, #1f0606)',
              textTransform: 'none',
              px: '24px',
              py: '8px',
              fontSize: '16px',
              borderRadius: '0',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#d4762e', boxShadow: 'none' },
              '&:disabled': { backgroundColor: 'var(--bg-disabled, #dfe3e6)' },
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </>
    </CommonModal>
  )
}
